import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { mutate } from '../lib/mutate';
import { DateInput } from './DateInput';
import { Field, inputClass, Modal } from './Modal';

export interface SchedulableGroup {
  groupKey: string;
  startDate: string;
  endDate: string;
  deal: string;
  memberCount: number;
}

/**
 * Moves a promotion group's run dates.
 *
 * Dates stay as YYYY-MM-DD strings the whole way through. They are calendar
 * dates with no timezone, and turning them into JavaScript Dates in the browser
 * is how a promotion ends up starting a day early once the clocks change.
 *
 * Mounted only while it is open. The rejection message lives in state here,
 * above the `<Modal>` that unmounts on close, so a dialog left mounted between
 * opens showed the last failure over a form nobody had touched yet.
 */
export function PromotionScheduleDialog({
  storeId,
  group,
  onClose,
}: {
  storeId: string;
  group: SchedulableGroup;
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: { startDate: string; endDate: string }) =>
      mutate.patch(`/stores/${storeId}/promotions/groups/${group.groupKey}/schedule`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['promotions', storeId] });
      setError(null);
      onClose();
    },
    onError: (caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not save.'),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);

    const form = new FormData(event.currentTarget);
    save.mutate({
      startDate: String(form.get('startDate')),
      endDate: String(form.get('endDate')),
    });
  }

  return (
    <Modal open title="Reschedule promotion" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <p className="mb-1 text-sm">{group.deal}</p>
        <p className="qs-figure mb-4 text-2xs text-ink-faint">
          {group.groupKey} · {group.memberCount} barcode
          {group.memberCount === 1 ? '' : 's'}
        </p>

        {/* Stacked on a phone: side by side, each field gets half of a 20rem
            dialog, which is not enough for a date at the 16px a touch device
            has to set its form controls at. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <Field label="Starts" htmlFor="startDate">
              <DateInput
                id="startDate"
                name="startDate"
                defaultValue={group.startDate}
                className={`${inputClass} qs-figure min-w-0`}
                required
              />
            </Field>
          </div>
          <div className="min-w-0">
            <Field label="Ends" htmlFor="endDate">
              <DateInput
                id="endDate"
                name="endDate"
                defaultValue={group.endDate}
                className={`${inputClass} qs-figure min-w-0`}
                required
              />
            </Field>
          </div>
        </div>

        <p className="mb-3 rounded border border-rule bg-sunken px-2.5 py-2 text-2xs text-ink-muted">
          Every barcode in this group moves together. A live promotion cannot be moved to a
          window that excludes today — deactivate it first.
        </p>

        {error && (
          <p role="alert" className="mb-3 text-xs text-down">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-ink-inverse hover:bg-accent-hover disabled:opacity-60"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
